import * as React from 'react';
import {
  SURFACE_MATTE_FILTER_ID,
  SURFACE_MATTE_GRADIENT_ID,
  SURFACE_MATTE_MASK_ID,
  SURFACE_MATTE_MASK_ALPHA_FILTER_ID,
  SURFACE_MATTE_ITEMS_ID,
} from '../../utils/surfaceFilter';
import { MIN_WORLD_UNIT_MM } from '../../utils/editorUnits';
import type { Bounds } from '../../Reducers/Configurator/types';

type SurfaceMatteFilterProps = {
  blurRadius: number;
  region: Bounds;
  designBounds: Bounds;
};

/**
 * Matte / Satin Surface Material Engine.
 * Same bevel structure as the gloss filter but with matte-appropriate values:
 * - Soft, wide drop shadow (r×0.90 vs gloss's r×0.15) for a diffuse, non-reflective look
 * - Warm-grey inner satin sheen (#b8a890) instead of sharp white — same gamma-sharpening
 *   trick as gloss so the effect is visible even on thin strokes, but softer (2.0/2.0 vs 3.5/2.5)
 * - Dark inner shadow on the opposing edge for depth without gloss contrast
 */
export default function SurfaceMatteFilter({ blurRadius, region, designBounds }: SurfaceMatteFilterProps) {
  const r = Math.max(blurRadius, MIN_WORLD_UNIT_MM);
  const gradientY1 = designBounds.y;
  const gradientY2 = designBounds.y + Math.max(designBounds.height, MIN_WORLD_UNIT_MM);

  return (
    <defs>
      <filter
        id={SURFACE_MATTE_FILTER_ID}
        filterUnits="userSpaceOnUse"
        x={region.x}
        y={region.y}
        width={Math.max(region.width, MIN_WORLD_UNIT_MM)}
        height={Math.max(region.height, MIN_WORLD_UNIT_MM)}
        colorInterpolationFilters="sRGB"
      >
        {/* Soft diffuse shadow — 6× wider blur than gloss, offset scaled to match */}
        <feDropShadow
          dx={r * 0.35}
          dy={r * 0.55}
          stdDeviation={r * 0.90}
          floodColor="#000000"
          floodOpacity="0.35"
          result="externalShadow"
        />

        {/* Same inner-face bevel setup as gloss — r*0.08 keeps thin strokes intact */}
        <feMorphology operator="erode" radius={r * 0.08} in="SourceAlpha" result="innerFace" />
        <feGaussianBlur in="innerFace" stdDeviation={r * 0.12} result="innerBlur" />

        {/* Satin highlight: same gamma-sharpening trick as gloss but softer (2.0/2.0 vs 3.5/2.5)
            and warm-grey (#b8a890) instead of white — visible on dark text, not reflective */}
        <feOffset dx={r * 0.14} dy={r * 0.14} in="innerBlur" result="innerShiftHi" />
        <feComposite in="innerBlur" in2="innerShiftHi" operator="out" result="satinRaw" />
        <feComposite in="satinRaw" in2="SourceAlpha" operator="in" result="satinMasked" />
        <feComponentTransfer in="satinMasked" result="softenedSatin">
          <feFuncA type="gamma" amplitude="2.0" exponent="2.0" />
        </feComponentTransfer>
        <feFlood floodColor="#b8a890" floodOpacity="0.75" result="satinLight" />
        <feComposite in="satinLight" in2="softenedSatin" operator="in" result="finalSatinShine" />

        {/* Inner shadow on opposing edge — depth without gloss-style contrast */}
        <feOffset dx={-r * 0.12} dy={-r * 0.12} in="innerBlur" result="innerShiftLo" />
        <feComposite in="innerBlur" in2="innerShiftLo" operator="out" result="shadowRaw" />
        <feComposite in="shadowRaw" in2="SourceAlpha" operator="in" result="shadowMasked" />
        <feFlood floodColor="#000008" floodOpacity="0.45" result="matteDark" />
        <feComposite in="matteDark" in2="shadowMasked" operator="in" result="finalInnerShadow" />

        <feMerge>
          <feMergeNode in="externalShadow" />
          <feMergeNode in="SourceGraphic" />
          <feMergeNode in="finalInnerShadow" />
          <feMergeNode in="finalSatinShine" />
        </feMerge>
      </filter>

      <filter id={SURFACE_MATTE_MASK_ALPHA_FILTER_ID}>
        <feFlood floodColor="#ffffff" />
        <feComposite in2="SourceAlpha" operator="in" />
      </filter>

      {/* Warm-grey ambient gradient — diffuse room light on a satin surface */}
      <linearGradient
        id={SURFACE_MATTE_GRADIENT_ID}
        gradientUnits="userSpaceOnUse"
        x1={0}
        y1={gradientY1}
        x2={0}
        y2={gradientY2}
      >
        <stop offset="0%" stopColor="#c8bfb0" stopOpacity={0.20} />
        <stop offset="30%" stopColor="#ffffff" stopOpacity={0.0} />
        <stop offset="70%" stopColor="#ffffff" stopOpacity={0.0} />
        <stop offset="100%" stopColor="#787060" stopOpacity={0.14} />
      </linearGradient>

      <mask
        id={SURFACE_MATTE_MASK_ID}
        maskUnits="userSpaceOnUse"
        x={region.x}
        y={region.y}
        width={Math.max(region.width, MIN_WORLD_UNIT_MM)}
        height={Math.max(region.height, MIN_WORLD_UNIT_MM)}
      >
        <g filter={`url(#${SURFACE_MATTE_MASK_ALPHA_FILTER_ID})`} pointerEvents="none">
          <use href={`#${SURFACE_MATTE_ITEMS_ID}`} pointerEvents="none" />
        </g>
      </mask>
    </defs>
  );
}
